import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  MessageSquare,
  Plus,
  Sparkles,
  Trash2,
  User,
} from 'lucide-react';
import { useActiveOrganizationId } from '@repo/api-client/react';
import { Page, PageHeader } from '@/features/common/PageHeader';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { EmptyState } from '@/components/ui/empty-state';
import { Alert } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useToast } from '@/components/ui/use-toast';
import { describeAppError } from '@/features/common/describeAppError';
import { useAiComposer } from '@/features/ai/AiComposerContext';
import {
  useAiConversation,
  useAiConversations,
  useDeleteAiConversation,
} from '@/features/ai/hooks';

interface UiMessagePart {
  type: string;
  text?: string;
}

interface UiMessage {
  id?: string;
  role: 'user' | 'assistant' | 'system' | 'tool';
  parts?: UiMessagePart[];
}

function extractText(message: UiMessage): string {
  if (!Array.isArray(message.parts)) return '';
  return message.parts
    .filter((p) => p.type === 'text' && typeof p.text === 'string')
    .map((p) => (p.text ?? '').trim())
    .join('\n')
    .trim();
}

export function AiHistoryScreen() {
  const orgId = useActiveOrganizationId();
  const { conversationId } = useParams<{ conversationId?: string }>();
  const navigate = useNavigate();
  const { conversations, loading, error } = useAiConversations(orgId);
  const { open: openComposer } = useAiComposer();
  const { deleteConversation, loading: deleting } = useDeleteAiConversation(orgId);
  const { toast } = useToast();
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);

  if (conversationId) {
    return (
      <ConversationDetail
        orgId={orgId ?? ''}
        conversationId={conversationId}
        onBack={() => navigate('/ai')}
      />
    );
  }

  return (
    <Page>
      <PageHeader
        title="AI assistant"
        description="Past conversations and a place to start a new one. Drafting invoices, suggesting reminders, and writing thank-you notes all run through the same composer."
        actions={
          <Button onClick={() => openComposer()}>
            <Plus className="size-4" /> New conversation
          </Button>
        }
      />

      {!orgId ? (
        <Alert variant="danger">
          Pick a business to see its AI conversations.
        </Alert>
      ) : loading ? (
        <div className="space-y-2">
          {Array.from({ length: 4 }).map((_, idx) => (
            <Skeleton key={idx} className="h-20 rounded-lg" />
          ))}
        </div>
      ) : error ? (
        <Alert variant="danger">{describeAppError(error).message}</Alert>
      ) : conversations.length === 0 ? (
        <Card>
          <CardContent>
            <EmptyState
              icon={<Sparkles className="size-7" />}
              title="No conversations yet"
              description="Open the composer and ask the assistant to draft your first invoice or write a reminder."
              actions={
                <Button onClick={() => openComposer()}>
                  <Plus className="size-4" /> Open composer
                </Button>
              }
            />
          </CardContent>
        </Card>
      ) : (
        <ul className="flex flex-col gap-2">
          {conversations.map((conv) => (
            <li
              key={conv.id}
              className="group relative flex items-start gap-3 rounded-lg border border-line bg-card p-4 transition hover:border-line-strong"
            >
              <div className="mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-full bg-secondary text-muted-foreground">
                <MessageSquare className="size-4" />
              </div>
              <button
                type="button"
                onClick={() => navigate(`/ai/${conv.id}`)}
                className="absolute inset-0 rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/40"
                aria-label={`Open ${conv.title ?? 'conversation'}`}
              />
              <div className="flex flex-1 flex-col gap-1">
                <div className="flex items-baseline justify-between gap-2">
                  <h4 className="text-sm font-medium text-foreground">
                    {conv.title ?? 'Untitled conversation'}
                  </h4>
                  <span className="shrink-0 text-xs text-muted-foreground">
                    {new Date(conv.updatedAt).toLocaleString()}
                  </span>
                </div>
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <Badge variant="default">{conv.messageCount} messages</Badge>
                  {conv.lastUsedModel ? (
                    <span className="font-mono text-[11px]">
                      {conv.lastUsedModel.split('/').pop()}
                    </span>
                  ) : null}
                </div>
              </div>
              <Button
                variant="ghost"
                size="icon"
                className="relative z-10 opacity-0 transition group-hover:opacity-100"
                aria-label="Delete conversation"
                onClick={(e) => {
                  e.stopPropagation();
                  setConfirmDelete(conv.id);
                }}
              >
                <Trash2 className="size-4 text-destructive" />
              </Button>
            </li>
          ))}
        </ul>
      )}

      <Dialog
        open={!!confirmDelete}
        onOpenChange={(open) => {
          if (!open) setConfirmDelete(null);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete conversation?</DialogTitle>
            <DialogDescription>
              The transcript will be removed for everyone in this business. AI usage
              already counted against this org's daily budget stays counted.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmDelete(null)}>
              Cancel
            </Button>
            <Button
              variant="danger"
              loading={deleting}
              onClick={async () => {
                if (!confirmDelete) return;
                try {
                  await deleteConversation(confirmDelete);
                  toast({ title: 'Conversation deleted', variant: 'success' });
                  setConfirmDelete(null);
                } catch (err) {
                  const desc = describeAppError(err);
                  toast({
                    title: desc.title,
                    description: desc.message,
                    variant: 'danger',
                  });
                }
              }}
            >
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Page>
  );
}

function ConversationDetail({
  orgId,
  conversationId,
  onBack,
}: {
  orgId: string;
  conversationId: string;
  onBack: () => void;
}) {
  const { conversation, loading, error } = useAiConversation(orgId, conversationId);

  return (
    <Page>
      <Button variant="link" size="sm" onClick={onBack} className="-ml-2 mb-4">
        <ArrowLeft className="size-4" /> All conversations
      </Button>
      {loading ? (
        <Skeleton className="h-[60vh] rounded-xl" />
      ) : error ? (
        <Alert variant="danger">{describeAppError(error).message}</Alert>
      ) : !conversation ? (
        <Alert>Conversation not found.</Alert>
      ) : (
        <>
          <PageHeader
            title={conversation.title ?? 'Untitled conversation'}
            description={`Started ${new Date(conversation.createdAt).toLocaleString()} · ${conversation.messageCount} messages`}
          />
          <div className="flex flex-col gap-4">
            {(conversation.messages as UiMessage[]).map((m, idx) => {
              const text = extractText(m);
              if (!text) return null;
              const isUser = m.role === 'user';
              return (
                <div
                  key={(m.id as string) ?? idx}
                  className={`flex gap-3 ${isUser ? 'flex-row-reverse' : ''}`}
                >
                  <div
                    className={`flex size-8 shrink-0 items-center justify-center rounded-full ${
                      isUser
                        ? 'bg-foreground text-background'
                        : 'bg-accent-soft text-accent'
                    }`}
                  >
                    {isUser ? <User className="size-4" /> : <Sparkles className="size-4" />}
                  </div>
                  <div
                    className={`max-w-[78%] rounded-2xl px-4 py-3 text-sm leading-relaxed ${
                      isUser
                        ? 'bg-foreground text-background'
                        : 'border border-line bg-card text-foreground'
                    }`}
                  >
                    <p className="whitespace-pre-wrap">{text}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}
    </Page>
  );
}
